import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { billsApi } from '@/api/bills-api'
import { useToast } from '@/hooks/use-toast'
import type { Bill, BillImpactScore, BillAction } from '@/types/bill'
import type { APISession } from '@/types/session'
import { isValidAPISession } from '@/types/session'
import { getJobByContext, JobContext } from '@umd-dit/jobs-component-react'
import { JobStatus } from '@/types/job'
import { useAuth } from '@/context/AuthContext'

// Define sorting related types
export type SortField = 'impact' | 'billNumber' | 'title' | 'lastModified' | 'crossfileBillNumber' | null
export type SortDirection = 'asc' | 'desc'

interface BillsWorkflowReturn {
  bills: Bill[]
  isLoading: boolean
  isError: boolean
  error: string | null
  selectedBills: string[]
  minImpactScore: BillImpactScore
  selectedSession: APISession | null
  setSelectedSession: (session: APISession) => void
  setMinImpactScore: (score: BillImpactScore) => void
  handleSelectBill: (billId: string) => void
  handleSelectAll: (bills: Bill[]) => void
  processBillAction: (billIds: string[], action: BillAction) => Promise<number>
  currentPage: number
  pageSize: number
  totalPages: number
  handlePageChange: (page: number) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  totalFilteredItems: number
  isFetchingNextPage: boolean
  processingActions: Record<string, BillAction>
  sortField: SortField
  sortDirection: SortDirection
  setSorting: (field: SortField, direction: SortDirection) => void
}

export const useBillsWorkflow = (
  initialMinImpactScore: BillImpactScore = 0,
  initialSession?: APISession
): BillsWorkflowReturn => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const [minImpactScore, setMinImpactScore] = useState<BillImpactScore>(initialMinImpactScore)
  const [selectedBills, setSelectedBills] = useState<string[]>([])
  const [selectedSession, setSelectedSession] = useState<APISession | null>(
    initialSession && isValidAPISession(initialSession) ? initialSession : null
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [processingActions, setProcessingActions] = useState<Record<string, BillAction>>({})
  const [sortField, setSortField] = useState<SortField>('impact')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const pageSize = 20 // Fixed page size

  useEffect(() => {
    setSelectedBills([])
  }, [minImpactScore, selectedSession, selectedWorkspaceId])

  useEffect(() => {
    if (initialSession && isValidAPISession(initialSession)) {
      console.log('🔄 Setting session from initialSession:', { initialSession })
      setSelectedSession(initialSession)
    }
  }, [initialSession])

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery])

  // Function to handle sorting
  const setSorting = useCallback((field: SortField, direction: SortDirection) => {
    setSortField(field)
    setSortDirection(direction)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Calculate impact value for sorting
  const getImpactValue = useCallback((bill: Bill): number => {
    const impact = bill.monitor_analysis?.summary?.overall_impact
    if (!impact) return -999 // No analysis should sort to the end
    
    // Create a numeric value for sorting that prioritizes type first, then score
    const typeValue = { positive: 1000, neutral: 0, negative: -1000 }
    return typeValue[impact.type] + impact.score
  }, [])

  // Sort bills based on current sort field and direction
  const sortBills = useCallback((bills: Bill[]): Bill[] => {
    if (!sortField) return bills

    return [...bills].sort((a, b) => {
      let comparison = 0

      if (sortField === 'impact') {
        const valueA = getImpactValue(a)
        const valueB = getImpactValue(b)
        comparison = valueB - valueA // Default descending for impact
      } else if (sortField === 'billNumber') {
        comparison = a.billNumber.localeCompare(b.billNumber)
      } else if (sortField === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '')
      } else if (sortField === 'lastModified') {
        comparison = a.lastModified.localeCompare(b.lastModified)
      } else if (sortField === 'crossfileBillNumber') {
        // Handle null values for crossfileBillNumber
        if (!a.crossfileBillNumber && !b.crossfileBillNumber) {
          comparison = 0
        } else if (!a.crossfileBillNumber) {
          comparison = 1 // Sort nulls to the end
        } else if (!b.crossfileBillNumber) {
          comparison = -1 // Sort nulls to the end
        } else {
          comparison = a.crossfileBillNumber.localeCompare(b.crossfileBillNumber)
        }
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [sortField, sortDirection, getImpactValue])


  const billsQuery = useInfiniteQuery({
    queryKey: ['bills', selectedSession, minImpactScore, selectedOrganizationId, selectedWorkspaceId],
    queryFn: async ({ pageParam }) => {
      console.log('[useBillsWorkflow] Fetching bills:', {
        session: selectedSession?.toString(),
        minImpactScore: minImpactScore,
        workspace: `${selectedOrganizationId}/${selectedWorkspaceId}`
      });

      if (!selectedSession) {
        console.warn('[useBillsWorkflow] No session selected');
        throw new Error('No session selected');
      }

      if (!selectedOrganizationId || !selectedWorkspaceId) {
        console.warn('[useBillsWorkflow] No workspace selected');
        throw new Error('No workspace selected');
      }

      // Use impact score filtering instead of workflow status
      const response = await billsApi.listBills({
        session: selectedSession,
        minImpactScore: minImpactScore,
        nextToken: pageParam || undefined
      })

      console.log('[useBillsWorkflow] Fetched', response.items.length, 'bills');
      return response
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    enabled: !!selectedSession && !!selectedOrganizationId && !!selectedWorkspaceId,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    gcTime: 3600000
  })

  // Automatically fetch pages until we have enough data for the current page
  // This should run only when currentPage changes and is stable
  const stableCurrentPage = useRef(currentPage);
  
  useEffect(() => {
    // Only execute when the page actually changes
    if (stableCurrentPage.current !== currentPage) {
      stableCurrentPage.current = currentPage;
      
      const pagesNeeded = currentPage;
      const pagesAvailable = billsQuery.data?.pages.length || 0;
      
      if (pagesAvailable < pagesNeeded && billsQuery.hasNextPage && !billsQuery.isFetchingNextPage) {
        console.log(`[useBillsWorkflow] Fetching more data for page ${currentPage}`);
        billsQuery.fetchNextPage();
      }
    }
  }, [currentPage, billsQuery])

  // Get all bills from the query
  // The backend API already filters by min_impact_score since we include it in the queryKey
  const allBills = useMemo(() => {
    return billsQuery.data?.pages.flatMap(page => page.items) || []
  }, [billsQuery.data])

  // Filter bills only based on search query
  const filteredBills = useMemo(() => {
    if (!searchQuery.trim()) return allBills;

    const query = searchQuery.trim().toUpperCase();
    return allBills.filter(bill => {
      const billNumber = bill.billNumber || '';
      return billNumber.startsWith(query);
    });
  }, [allBills, searchQuery])

  // Apply sorting to filtered bills
  const sortedBills = useMemo(() => {
    return sortBills(filteredBills)
  }, [filteredBills, sortBills])

  // Get bills for the current page
  const getCurrentPageBills = (): Bill[] => {
    const startIdx = (currentPage - 1) * pageSize
    const endIdx = startIdx + pageSize
    const result = sortedBills.slice(startIdx, endIdx)

    console.log(`[useBillsWorkflow] getCurrentPageBills: Returning ${result.length} bills for page ${currentPage} (${startIdx}-${endIdx}) from ${sortedBills.length} total bills`)

    return result
  }

  const totalItems = sortedBills.length
  const totalPages = Math.ceil(totalItems / pageSize)

  // Ensure current page is valid after filtering
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages])

  useEffect(() => {
    if (billsQuery.hasNextPage && !billsQuery.isFetchingNextPage) {
      billsQuery.fetchNextPage();
    }
  }, [billsQuery.data, billsQuery.hasNextPage, billsQuery.isFetchingNextPage])

  // Removed updateBillsWorkflowMutation - no longer using workflow status

  const processBillActionMutation = useMutation({
    mutationFn: async ({ billIds, action }: { billIds: string[], action: BillAction }) => {
      if (!selectedSession) throw new Error('No session selected')
      if (!selectedOrganizationId || !selectedWorkspaceId) throw new Error('No workspace selected')

      const results = await Promise.all(
        billIds.map(billId =>
          billsApi.processBillAction({
            session: selectedSession, // Still needed for record keeping
            billNumber: billId,
            action: action
          })
        )
      )
      billIds.forEach(billId => {
        setProcessingActions(prev => ({
          ...prev,
          [billId]: action
        }))
      })
      return {
        billIds,
        action,
        jobIds: results.map(r => r.job_id)
      }
    },
    onSuccess: ({ billIds, action, jobIds }) => {
      billIds.forEach((billId, index) => {
        const jobId = jobIds[index]
        if (!jobId) return

        // Add a delay before starting to poll to give the job time to be created
        setTimeout(() => {
          const intervalId = setInterval(async () => {
            try {
              // Create job context with workspace information
              const jobContext: JobContext = {
                appId: "legislative-review",
                orgId: selectedOrganizationId || undefined,
                wsId: selectedWorkspaceId || undefined
              }
              console.log('Using context ' + JSON.stringify(jobContext))

              // Use the workspace-aware job polling
              const jobStatus = await getJobByContext(jobContext, jobId)

              // If job is complete or errored, stop polling
              if (jobStatus.status === JobStatus.COMPLETED || jobStatus.status === JobStatus.ERROR) {
                clearInterval(intervalId)

                setProcessingActions(prev => {
                  const newState = { ...prev }
                  delete newState[billId]
                  return newState
                })

                if (jobStatus.status === JobStatus.COMPLETED) {
                  console.log(`[useBillsWorkflow] Job completed for bill ${billId}`)
                  
                  try {
                    // Fetch the updated bill
                    const bill = await billsApi.getBill(selectedSession!, billId)
                    console.log(`[useBillsWorkflow] Updated bill data:`, {
                      billNumber: bill.billNumber,
                      impactScore: bill.impactScore,
                      hasMonitorAnalysis: !!bill.monitor_analysis,
                      hasLegislativeReview: !!bill.legislativeReviewS3Key
                    })

                    // Update the cache with the new bill data
                    // Since we're using impact score filtering, we need to update the appropriate cache
                    const queryKey = ['bills', selectedSession, minImpactScore, selectedOrganizationId, selectedWorkspaceId]
                    
                    queryClient.setQueryData(queryKey, (oldData: any) => {
                      if (!oldData?.pages) return oldData
                      
                      const updatedData = {
                        ...oldData,
                        pages: oldData.pages.map((page: any) => ({
                          ...page,
                          items: page.items.map((item: Bill) => {
                            if (item.billNumber === billId) {
                              return bill
                            }
                            return item
                          })
                        }))
                      }
                      
                      return updatedData
                    })
                    
                    console.log(`[useBillsWorkflow] Updated bill ${billId} in cache`)
                  } catch (error) {
                    console.error(`[useBillsWorkflow] Error updating bill cache for ${billId}:`, error)
                    // Fallback: invalidate cache to ensure consistency
                    queryClient.invalidateQueries({
                      queryKey: ['bills', selectedSession, minImpactScore, selectedOrganizationId, selectedWorkspaceId],
                      exact: true
                    })
                  }
                } else if (jobStatus.status === JobStatus.ERROR) {
                  toast({
                    title: "Processing Failed",
                    description: `Failed to process bill ${billId}`,
                    variant: "destructive"
                  })
                }
              }
            } catch (error) {
              console.error('Error polling for job status:', error)
            }
          }, 2000)

          // Clear interval after 5 minutes to prevent infinite polling
          setTimeout(() => {
            clearInterval(intervalId)
            setProcessingActions(prev => {
            const newState = { ...prev }
            delete newState[billId]
            return newState
          })
        }, 300000)}, 3000); // Initial delay of 3 seconds before starting to poll
      })

      setSelectedBills([])
      toast({
        title: "Processing Started",
        description: `AI ${action} will be generated for ${billIds.length} bill${billIds.length === 1 ? '' : 's'}`
      })
    },
    onError: (error) => {
      console.error('Failed to process bill action:', error)
      toast({
        title: "Error",
        description: "Failed to start bill processing",
        variant: "destructive"
      })
    }
  })

  const handleSelectBill = useCallback((billId: string) => {
    setSelectedBills(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    )
  }, [])

  const handleSelectAll = useCallback((bills: Bill[]) => {
    setSelectedBills(prev => 
      prev.length === bills.length 
        ? []
        : bills.map(b => b.billNumber)
    )
  }, [])

  const processBillAction = async (billIds: string[], action: BillAction) => {
    await processBillActionMutation.mutateAsync({ billIds, action })
    return billIds.length
  }

  // Use a more comprehensive loading state that includes both initial loading and fetching additional pages
  const isLoading = billsQuery.isPending || billsQuery.isFetching || !selectedSession

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }

  // Generate the final bills to return
  const billsToReturn = getCurrentPageBills()

  // Debugging log to track what's being returned
  console.log(`[useBillsWorkflow] Returning hook data:`, {
    billsCount: billsToReturn.length,
    isLoading,
    isError: billsQuery.isError,
    minImpactScore,
    totalItems,
    currentPage,
    totalPages
  })

  return {
    bills: billsToReturn,
    isLoading,
    isError: billsQuery.isError,
    error: billsQuery.isError ? 'Failed to load bills' : null,
    selectedBills,
    minImpactScore,
    selectedSession,
    setSelectedSession,
    setMinImpactScore,
    handleSelectBill,
    handleSelectAll,
    processBillAction,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems: totalItems,
    isFetchingNextPage: billsQuery.isFetchingNextPage,
    processingActions,
    sortField,
    sortDirection,
    setSorting
  }
}